import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogIn, LayoutDashboard, LogOut, ShoppingBag } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";
import { Logo } from "@/components/brand/Logo";
import { useCustomerAuth } from "@/customer/AuthContext";
import { useCart } from "@/customer/CartContext";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useCustomerAuth();
  const { count: cartCount } = useCart();
  const navigate = useNavigate();
  const closeMenu = () => setOpen(false);
  const onLogout = () => { logout(); closeMenu(); navigate("/"); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`header-in fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div
        className={`mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 transition-all duration-500 ${
          scrolled ? "" : ""
        }`}
      >
        <div
          className={`flex items-center justify-between rounded-full transition-all duration-500 ${
            scrolled
              ? "glass-strong px-4 sm:px-5 py-2 chrome-border"
              : "px-2 py-1"
          }`}
        >
          <Logo size={scrolled ? 38 : 44} />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-[12px] font-medium tracking-[0.22em] uppercase transition-colors ${
                    isActive ? "text-white" : "text-white/65 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="inline-flex flex-col items-center gap-1.5">
                    {n.label}
                    <span
                      className={`h-px w-6 transition-all duration-500 ${
                        isActive
                          ? "bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] opacity-100"
                          : "bg-white/0 opacity-0"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Link to="/customer/cart" data-testid="nav-cart" className="relative inline-flex items-center justify-center w-10 h-10 rounded-full glass chrome-border text-white hover:text-[#ff2e93] transition-colors" aria-label="Cart">
                  <ShoppingBag className="w-4 h-4" />
                  {cartCount > 0 && (
                    <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-[10px] font-display text-white flex items-center justify-center">{cartCount}</span>
                  )}
                </Link>
                <Link to="/customer" data-testid="nav-link-dashboard" className="btn-ghost !py-2.5 !px-5 !text-[11px]">
                  <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
                </Link>
                <button onClick={onLogout} data-testid="nav-logout" className="btn-ghost !py-2.5 !px-5 !text-[11px]">
                  <LogOut className="w-3.5 h-3.5" /> Logout
                </button>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-link-login" className="btn-ghost !py-2.5 !px-5 !text-[11px]">
                <LogIn className="w-3.5 h-3.5" /> Login / Register
              </Link>
            )}
            <Link to="/hire" data-testid="nav-cta-hire" className="btn-primary cta-pulse !py-2.5 !px-5 !text-[11px]">
              Hire Us
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full glass chrome-border text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div data-testid="mobile-menu-panel" className="mm-panel lg:hidden mt-3 glass-strong chrome-border rounded-2xl p-5">
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    onClick={closeMenu}
                    data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between py-3 px-2 border-b border-white/5 text-sm tracking-[0.18em] uppercase ${
                        isActive ? "text-white" : "text-white/70"
                      }`
                    }
                  >
                    {n.label}
                    <span className="text-[#ff2e93]">→</span>
                  </NavLink>
                </li>
              ))}
              <li>
                {user ? (
                  <>
                    <Link to="/customer/cart" onClick={closeMenu} data-testid="mobile-nav-cart" className="flex items-center justify-between py-3 px-2 text-sm tracking-[0.18em] uppercase text-white/70">Cart {cartCount > 0 && <span data-testid="mobile-nav-cart-count" className="ml-auto mr-2 text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white">{cartCount}</span>}<span className="text-[#18d3ff]">→</span></Link>
                    <Link to="/customer" onClick={closeMenu} data-testid="mobile-nav-dashboard" className="flex items-center justify-between py-3 px-2 text-sm tracking-[0.18em] uppercase text-white/70">Dashboard <span className="text-[#18d3ff]">→</span></Link>
                    <button onClick={onLogout} data-testid="mobile-nav-logout" className="w-full text-left flex items-center justify-between py-3 px-2 text-sm tracking-[0.18em] uppercase text-white/70">Logout <span className="text-[#ff2e93]">→</span></button>
                  </>
                ) : (
                  <Link to="/auth" onClick={closeMenu} data-testid="mobile-nav-link-login" className="flex items-center justify-between py-3 px-2 text-sm tracking-[0.18em] uppercase text-white/70">
                    Login / Register <span className="text-[#18d3ff]">→</span>
                  </Link>
                )}
              </li>
            </ul>
            <Link to="/hire" data-testid="mobile-nav-cta-hire" className="btn-primary w-full mt-4">
              Hire Us
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
